import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink } from 'lucide-react';
import { useBranding, useHeaderNav, useSiteLayout } from '../../contexts/SiteContext';
import { useAuth } from '../../contexts/AuthContext';
import { useViewTransition } from '../../hooks/useViewTransition';
import { MobileMenu } from './MobileMenu';
import type { CmsNavItem } from '../../types/cms';

function BrandLogo() {
  const branding = useBranding();
  const navigate = useViewTransition();
  return (
    <button
      onClick={() => navigate('/')}
      className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
    >
      {branding.logoUrl ? (
        <img src={branding.logoUrl} alt={branding.name} className="h-7 w-auto object-contain" />
      ) : (
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {branding.name.charAt(0).toUpperCase()}
        </div>
      )}
      <span className="text-sm font-semibold text-zinc-900">{branding.name}</span>
    </button>
  );
}

function NavLink({ item }: { item: CmsNavItem }) {
  const location = useLocation();
  const navigate = useViewTransition();
  const isActive = !item.isExternal && location.pathname === item.path;

  if (item.isExternal) {
    const safeHref =
      item.path.startsWith('http://') || item.path.startsWith('https://') ? item.path : '#';
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 transition-colors"
      >
        {item.label}
        <ExternalLink size={11} className="text-zinc-400" />
      </a>
    );
  }

  return (
    <button
      onClick={() => navigate(item.path)}
      className={[
        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
        isActive
          ? 'bg-[--color-primary] text-white'
          : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50',
      ].join(' ')}
    >
      {item.label}
    </button>
  );
}

export function SiteHeader() {
  const layout = useSiteLayout();
  const headerItems = useHeaderNav();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useViewTransition();
  const [mobileOpen, setMobileOpen] = useState(false);

  const stickyClass = layout.headerStyle !== 'static' ? 'sticky top-0 z-30' : '';
  const transparentClass =
    layout.headerStyle === 'transparent'
      ? 'bg-transparent border-transparent'
      : 'bg-white border-zinc-100';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      <header
        className={`border-b ${stickyClass} ${transparentClass}`}
        style={{ viewTransitionName: 'site-header' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <BrandLogo />

          {/* Desktop nav */}
          {layout.headerStyle !== 'minimal' && (
            <nav className="hidden sm:flex items-center gap-1 flex-1">
              {headerItems.map((item) => (
                <NavLink key={item.id} item={item} />
              ))}
            </nav>
          )}

          {/* Right slot: auth */}
          <div className="hidden sm:flex items-center gap-2">
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors px-3 py-1.5"
              >
                Esci
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--color-primary)' }}
              >
                Accedi
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="sm:hidden p-1.5 text-zinc-500 hover:text-zinc-900 transition-colors"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      <MobileMenu isOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
